import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints';

import { Patient, UpdatePatientDto } from '@/types/patient';
import {
  PatientDetailsResponse,
  PatientSettingsRequest,
  PatientSettingsResponse
} from '@/types/reportsPatient';

import { transmissionReportApiSlice } from './transmissionReportsApiSlice';

export const reportsPatientApiSlice = createApi({
  reducerPath: 'reportsPatientApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: ['ReportPatient'],
  endpoints: (builder) => ({
    getReportPatientDetails: builder.query<PatientDetailsResponse, string>({
      query: (patientId) => ({
        url: `/patients/v2/${patientId}`,
        method: 'GET'
      }),
      keepUnusedDataFor: 1,
      providesTags: (_result, _error, patientId) => [{ type: 'ReportPatient', id: patientId }]
    }),
    getPatientSettings: builder.query<PatientSettingsResponse, string>({
      query: (patientId) => ({
        url: `/patients/v2/${patientId}/patient-settings`,
        method: 'GET'
      }),
      keepUnusedDataFor: 1,
      providesTags: (_result, _error, patientId) => [{ type: 'ReportPatient', id: patientId }]
    }),
    updatePatientSettings: builder.mutation<PatientSettingsResponse, PatientSettingsRequest>({
      query: ({ reportId, patientSettings }) => ({
        url: `/patients/v2/${reportId}/patient-settings`,
        method: 'POST',
        body: patientSettings
      })
    }),
    updatePatient: builder.mutation<{ patient: Patient }, UpdatePatientDto>({
      query: ({ patient }) => ({
        url: `${API_ENDPOINTS.PATIENTS.LIST}/${patient.id}`,
        method: 'PATCH',
        body: {
          referringProvider: patient?.referringProvider?.NPI,
          implantingEp: patient?.implantingEp?.NPI,
          implantingEpFreeText: patient?.implantingEpFreeText,
          referringPhysician: patient?.referringPhysician,
          oac: patient?.oac,
          practitionerId: patient?.practitioner?.id,
          diagnosisOption: patient?.diagnosisOption,
          monitorReason: patient?.monitorReason,
          birthDate: patient?.birthDate,
          sex: patient?.sex,
          mrn2: patient?.mrn2,
          inClinicPractitionerId: patient?.inClinicPractitioner?.id,
          patientStatus: patient?.patientStatus,
          statusReason: patient?.statusReason,
          statusStartDate: patient?.statusStartDate,
          statusEndDate: patient?.statusEndDate,
          statusOtherReason: patient?.statusOtherReason
        }
      }),
      async onQueryStarted({ patient, activeReportId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          if (patient?.id) {
            dispatch(
              reportsPatientApiSlice.util.invalidateTags([
                { type: 'ReportPatient', id: patient.id }
              ])
            );
          }

          if (activeReportId) {
            dispatch(
              transmissionReportApiSlice.util.invalidateTags([
                { type: API_SLICE.TransmissionReport, id: activeReportId }
              ])
            );
          }
        } catch (error) {
          console.error('Error in Update Patient', error);
        }
      }
    })
  })
});

export const {
  useGetReportPatientDetailsQuery,
  useGetPatientSettingsQuery,
  useUpdatePatientSettingsMutation,
  useUpdatePatientMutation
} = reportsPatientApiSlice;
